import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext.jsx';
import { deleteResearchTool } from '../../lib/researchTools.js';

export default function ResearchToolDetailModal({ item, onClose, onDeleted }) {
  const { t } = useTranslation();
  const { isAdmin, isActive } = useAuth();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!item) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [item, onClose]);

  if (!item) return null;

  async function handleDelete() {
    setBusy(true);
    setError(null);
    try {
      await deleteResearchTool(item.id);
      onClose();
      onDeleted?.(item.id);
    } catch (err) {
      setError(err.message ?? String(err));
    } finally {
      setBusy(false);
    }
  }

  function handleEdit() {
    onClose();
    navigate(`/research-tools/${item.id}/edit`);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-navy-deep/80 p-4 backdrop-blur"
      onClick={onClose}
    >
      <div
        className="relative my-12 w-full max-w-2xl rounded-xl border border-white/10 bg-navy-base/95 p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full border border-white/10 bg-navy-mid/40 px-3 py-1 text-sm text-ink-secondary hover:border-gold/40 hover:text-gold-light"
          aria-label={t('common.cancel')}
        >
          ×
        </button>

        <div className="mb-2 flex flex-wrap items-baseline gap-3">
          <span className="text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-gold">
            {t(`researchTools.types.${item.type}`)}
          </span>
          {item.institution && <span className="text-sm text-ink-muted">{item.institution}</span>}
        </div>

        <h2 className="font-serif text-2xl font-semibold leading-snug text-cream">{item.name}</h2>

        {item.description && (
          <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink-primary">
            {item.description}
          </p>
        )}

        <dl className="mt-6 grid grid-cols-1 gap-y-3 text-sm md:grid-cols-[140px_1fr]">
          <Field label={t('researchTools.detail.url')} value={item.url} link />
        </dl>

        {item.url && (
          <div className="mt-6">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm"
            >
              {t('researchTools.detail.openResource')}
            </a>
          </div>
        )}

        {isActive && (
          <footer className="mt-8 flex flex-wrap items-center justify-end gap-3 border-t border-white/5 pt-6">
            {error && <p className="mr-auto text-sm text-red-300">{error}</p>}
            {confirmDelete ? (
              <>
                <span className="text-sm text-ink-secondary">{t('researchTools.detail.confirmDelete')}</span>
                <button type="button" className="btn-ghost text-sm" onClick={() => setConfirmDelete(false)} disabled={busy}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="rounded-md border border-red-500/50 bg-red-950/30 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                  onClick={handleDelete}
                  disabled={busy}
                >
                  {busy ? t('common.loading') : t('common.delete')}
                </button>
              </>
            ) : (
              <>
                <button type="button" className="btn-ghost text-sm" onClick={handleEdit}>
                  {isAdmin ? t('researchTools.detail.editEntry') : t('researchTools.detail.suggestEdit')}
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="rounded-md border border-red-500/40 px-4 py-2 text-sm font-medium text-red-200 hover:border-red-400 hover:text-red-100"
                    onClick={() => setConfirmDelete(true)}
                  >
                    {t('common.delete')}
                  </button>
                )}
              </>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, link }) {
  if (!value) return null;
  const display = link ? (
    <a href={value} target="_blank" rel="noopener noreferrer" className="text-gold-light hover:underline break-all">
      {value}
    </a>
  ) : value;
  return (
    <>
      <dt className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">{label}</dt>
      <dd className="text-ink-primary">{display}</dd>
    </>
  );
}
